/**
 * DiarioGiornalieroTabRebuilt — Diario Giornaliero versione COMPLETA
 * ricostruita da SafeMode (22/02/2026) e ESTESA come workspace
 * operativo collaborativo (26/02/2026 — pre-pubblicazione).
 *
 * Funzionalità:
 *  - Lista note con filtri (search, tipo, stato, operatore autore,
 *    destinatario, cliente, anno/mese da-a).
 *  - Modale Nuova/Modifica con:
 *      • Cliente: ricerca con datalist HTML5 (autocomplete nativo), OBBLIGATORIO
 *      • Operatore: bloccato all'utente loggato (read-only)
 *      • Area Operativa: OBBLIGATORIO (Gestione Studio / Gestione Agenzia)
 *      • Pratica Collegata: opzionale, filtrata dalle pratiche ATTIVE del cliente
 *      • Tipo contatto (incluso "Posta"), Data, Ora, Destinatario, Contenuto
 *      • Pulsante "Stampa Scheda" che apre la scheda operativa completa
 *      • Sezione Cronologia (history) per note esistenti
 *      • Sezione Risposte Operative (threaded replies) per note esistenti
 *      • Azioni "Crea Scadenza" / "Crea Pratica" che prefillano i nuovi record
 *  - Workflow: la nota nasce ATTIVA, chiusura solo esplicita via ✓ riga.
 *  - Stampa completa: nota + risposte operative + cronologia + chiusura.
 *
 * Stabilità React 19:
 *   ❌ Nessun Radix Select/Dialog/Popover → <select>/<datalist> nativi + InlineModal CSS
 *   ❌ Nessun React.memo / useDeferredValue / startTransition
 *   ✅ Multi-tbody pattern, empty-state stabile, toast post-success.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/api";
import { IsolatedTextarea } from "@/components/common/IsolatedTextarea";
import { DebouncedSearchInput } from "@/components/common/DebouncedSearchInput";

const CONTACT_TYPES = [
  { value: "Telefonata", color: "#dbeafe", text: "#1e40af" },
  { value: "Email",      color: "#fef3c7", text: "#92400e" },
  { value: "WhatsApp",   color: "#dcfce7", text: "#166534" },
  { value: "Posta",      color: "#fee2e2", text: "#991b1b" },
  { value: "Presenza",   color: "#ede9fe", text: "#5b21b6" },
];

const AREA_OPTIONS = [
  { value: "studio", label: "Gestione Studio" },
  { value: "agenzia", label: "Gestione Agenzia" },
];

const MONTHS_IT = [
  ["01", "Gennaio"], ["02", "Febbraio"], ["03", "Marzo"], ["04", "Aprile"],
  ["05", "Maggio"], ["06", "Giugno"], ["07", "Luglio"], ["08", "Agosto"],
  ["09", "Settembre"], ["10", "Ottobre"], ["11", "Novembre"], ["12", "Dicembre"],
];
const lastDayOfMonth = (year, monthNum) => {
  const d = new Date(Number(year), Number(monthNum), 0).getDate();
  return String(d).padStart(2, "0");
};
const todayISO = () => new Date().toISOString().slice(0, 10);
const nowHHMM = () => new Date().toTimeString().slice(0, 5);

const PAGE_SIZES = [10, 30, 50, 100];

const isPracticeActive = (p) => {
  const status = String(p?.status || "").toLowerCase();
  return status !== "completata" && status !== "chiusa" && status !== "annullata";
};

function InlineModal({ open, onClose, title, children, footer, wide = false, closeOnBackdrop = true }) {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 50, padding: 16,
      }}
      onClick={closeOnBackdrop ? onClose : undefined}
      data-testid="inline-modal-backdrop"
    >
      <div
        style={{
          background: "white", borderRadius: 8, boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
          width: "100%", maxWidth: wide ? 920 : 480, maxHeight: "92vh",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{title}</h3>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748b", padding: 0, lineHeight: 1 }} aria-label="Chiudi">×</button>
        </div>
        <div style={{ padding: 16, overflowY: "auto", flex: 1 }}>{children}</div>
        {footer && <div style={{ padding: "10px 16px", borderTop: "1px solid #e2e8f0", display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>{footer}</div>}
      </div>
    </div>
  );
}

const inputStyle = { padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 13, width: "100%", boxSizing: "border-box" };
const inputReadOnlyStyle = { ...inputStyle, background: "#f1f5f9", color: "#475569", cursor: "not-allowed" };
const labelStyle = { display: "block", fontSize: 11, textTransform: "uppercase", color: "#64748b", marginBottom: 2 };
const btnPrimary = { padding: "6px 12px", background: "#0d9488", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 13, fontWeight: 500 };
const btnSecondary = { padding: "6px 12px", background: "white", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 4, cursor: "pointer", fontSize: 13 };
const btnGhost = { background: "none", border: "none", color: "#475569", cursor: "pointer", padding: "2px 6px", fontSize: 13 };
const btnAccent = { padding: "6px 12px", background: "#1d4ed8", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 13, fontWeight: 500 };

const emptyNoteForm = (currentOperator) => ({
  contactDate: todayISO(),
  contactTime: nowHHMM(),
  contactType: "Telefonata",
  clientId: "",
  clientName: "",
  clientQuery: "",
  caseId: "",
  areaType: "",
  content: "",
  operatorId: currentOperator?.id || "",
  operatorName: currentOperator?.fullName || currentOperator?.name || "",
  destinatarioId: "",
});

// ─── Stampa scheda operativa completa ────────────────────────────────
function printOperationalSheet(c, helpers) {
  const { getOperatorName, getClientLabel, getCaseLabel } = helpers;
  const w = window.open("", "_blank", "width=900,height=900");
  if (!w) return false;
  const esc = (s) => String(s ?? "").replace(/[<>&]/g, (ch) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[ch]));
  const d = c.contactDate ? new Date(c.contactDate + "T00:00:00").toLocaleDateString("it-IT") : "—";
  const history = Array.isArray(c.history) ? c.history : [];
  const replies = history.filter((h) => (h.action || "") === "reply");
  const audit = history.filter((h) => (h.action || "") !== "reply");
  const tsFmt = (s) => s ? new Date(s).toLocaleString("it-IT") : "—";
  const areaLabel = c.areaType === "studio" ? "Gestione Studio" : (c.areaType === "agenzia" ? "Gestione Agenzia" : "—");
  const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"/>
    <title>Scheda Diario — ${esc(c.clientName || "")}</title>
    <style>
      body{font-family:Arial,sans-serif;padding:22px;max-width:820px;margin:0 auto;color:#1e293b;font-size:12px}
      h1{margin:0 0 4px;font-size:20px}
      h2{font-size:14px;margin:18px 0 6px;color:#0f172a;border-bottom:1px solid #cbd5e1;padding-bottom:3px}
      .meta{color:#64748b;font-size:11px;margin-bottom:14px}
      table{width:100%;border-collapse:collapse;margin-top:4px}
      td{padding:5px 8px;border-bottom:1px solid #e2e8f0;font-size:11px;vertical-align:top}
      .label{background:#f8fafc;width:34%;color:#475569;font-weight:600}
      .longtext{white-space:pre-wrap;background:#fafafa;border:1px solid #e2e8f0;padding:10px;border-radius:4px;font-size:11.5px;margin-top:6px}
      .reply{border-left:3px solid #1d4ed8;background:#eff6ff;padding:8px 10px;border-radius:0 4px 4px 0;margin:6px 0;font-size:11px}
      .reply .who{font-weight:600;color:#1e3a8a;font-size:11px}
      .reply .when{color:#64748b;font-size:10px;margin-left:6px}
      .audit{font-size:10.5px;color:#475569;margin:2px 0;padding-left:8px;border-left:2px solid #e2e8f0}
      .badge{display:inline-block;padding:2px 6px;border-radius:3px;font-size:10px;font-weight:600}
      .badge-closed{background:#dcfce7;color:#166534}
      .badge-open{background:#dbeafe;color:#1e40af}
      @media print{button{display:none}}
    </style></head><body>
    <h1>Scheda Diario Giornaliero</h1>
    <div class="meta">Stampato il ${esc(new Date().toLocaleString("it-IT"))} ·
      <span class="${c.completedAt ? "badge badge-closed" : "badge badge-open"}">${c.completedAt ? "Chiusa" : "In corso"}</span></div>

    <h2>Intestazione</h2>
    <table>
      <tr><td class="label">Data / Ora</td><td>${esc(d)} ${esc(c.contactTime || "")}</td></tr>
      <tr><td class="label">Tipo contatto</td><td>${esc(c.contactType || "—")}</td></tr>
      <tr><td class="label">Area operativa</td><td>${esc(areaLabel)}</td></tr>
      <tr><td class="label">Cliente</td><td>${esc(getClientLabel(c.clientId, c.clientName))}</td></tr>
      <tr><td class="label">Pratica collegata</td><td>${esc(getCaseLabel(c.caseId))}</td></tr>
      <tr><td class="label">Operatore (autore)</td><td>${esc(getOperatorName(c.operatorId) || c.operatorName || "—")}</td></tr>
      <tr><td class="label">Destinatario</td><td>${esc(getOperatorName(c.destinatarioId) || c.destinatarioName || "—")}</td></tr>
    </table>

    <h2>Contenuto originale</h2>
    <div class="longtext">${esc(c.content || "(vuoto)")}</div>

    ${replies.length > 0 ? `<h2>Risposte operative (${replies.length})</h2>${replies.map((r) => `
      <div class="reply">
        <div><span class="who">${esc(getOperatorName(r.operatorId) || r.operatorName || "Operatore")}</span>
          <span class="when">${esc(tsFmt(r.timestamp))}</span></div>
        <div style="margin-top:4px">${esc(r.details || r.newValue || r.note || r.content || "(testo risposta non disponibile)")}</div>
      </div>`).join("")}` : `<h2>Risposte operative</h2><p style="color:#94a3b8;font-style:italic;font-size:11px">Nessuna risposta operativa.</p>`}

    <h2>Cronologia (audit trail)</h2>
    ${audit.length > 0 ? audit.map((h) => `
      <div class="audit"><strong>${esc(tsFmt(h.timestamp))}</strong> · ${esc(h.action || "—")} ·
        ${esc(getOperatorName(h.operatorId) || h.operatorName || "Sistema")}
        ${h.note ? ` — ${esc(h.note)}` : ""}
      </div>`).join("") : `<p style="color:#94a3b8;font-style:italic;font-size:11px">Nessuna modifica registrata.</p>`}

    ${c.completedAt ? `<h2>Chiusura</h2><table>
      <tr><td class="label">Chiusa il</td><td>${esc(tsFmt(c.completedAt))}</td></tr>
      ${c.completedByOperatorId ? `<tr><td class="label">Chiusa da</td><td>${esc(getOperatorName(c.completedByOperatorId) || c.completedByOperatorName || "—")}</td></tr>` : ""}
    </table>` : ""}

    <script>window.onload=()=>setTimeout(()=>window.print(),250)</script>
    </body></html>`;
  w.document.open(); w.document.write(html); w.document.close();
  return true;
}

// ─── Memoized option lists (Wave 2 — rev2, 30/06/2026) ────────────
// Liste statiche / che non dipendono da editForm vengono spostate in
// componenti memoizzati così l'identità dei nodi figli del modal non
// cambia ad ogni keystroke. Senza queste, React 19 in StrictMode
// dev-build ricostruisce le 127 <option> del datalist + le 12 option
// dei <select> ogni 16ms durante la digitazione, generando ~30ms di
// jitter per frame.

const ClientsDataList = React.memo(function ClientsDataList({ clients }) {
  return (
    <datalist id="diary-clients-list">
      {clients.map((c) => (
        <option key={c.id} value={c.fullName || c.name} />
      ))}
    </datalist>
  );
});

const OperatorsSelectOptions = React.memo(function OperatorsSelectOptions({ operators }) {
  return (
    <>
      <option value="">—</option>
      {operators.map((o) => (
        <option key={o.id} value={o.id}>{o.fullName || o.name || o.id}</option>
      ))}
    </>
  );
});

const ContactTypeSelectOptions = React.memo(function ContactTypeSelectOptions() {
  return (
    <>
      {CONTACT_TYPES.map((t) => (
        <option key={t.value} value={t.value}>{t.value}</option>
      ))}
    </>
  );
});

const AreaSelectOptions = React.memo(function AreaSelectOptions() {
  return (
    <>
      <option value="">— Seleziona —</option>
      {AREA_OPTIONS.map((a) => (
        <option key={a.value} value={a.value}>{a.label}</option>
      ))}
    </>
  );
});

// ─── DiaryNoteEditor (Wave 2 perf refactor, 28/06/2026) ────────────
// Componente figlio che possiede internamente `editForm`/`replyText`
// così la digitazione nelle textarea NON ri-renderizza la tabella di
// 50 righe e i 11 filtri del genitore. Il genitore riceve solo
// callback ad eventi (save/reply/close/createScadenza/createPratica)
// e passa lo snapshot iniziale `initialForm` una volta sola, all'apertura.
//
// Vincoli React 19 mantenuti identici al parent:
//   ❌ Nessun Radix Select/Dialog/Popover
//   ❌ Nessun React.memo (non serve: il componente è già il "leaf"
//      che cambia ad ogni keystroke; memoizzarlo non darebbe nulla)
//   ❌ Nessun useDeferredValue / startTransition
//   ✅ Stesso InlineModal CSS del parent
//   ✅ Stessa logica di dirty-check e prompt window.confirm()
function DiaryNoteEditor({
  open,
  editing,                 // null oppure {id, history, ...} (sorgente di verità per cronologia)
  initialForm,             // snapshot del form generato dal parent in openCreate/openEdit
  clients = [],
  cases = [],
  operators = [],
  currentOperator,
  clientById,
  getOperatorName,
  getClientLabel,
  onClose,                 // chiamato quando il dialog deve chiudersi (passa control al parent)
  onSaved,                 // (savedNote, isNew) → il parent merge'a in items e chiude
  onReplied,               // (updatedNote) → il parent aggiorna items + editing
  onOpenNewClient,         // dal parent (di solito App.js) — apre il CaseDialog "Nuovo Cliente"
  onCreaScadenzaFromNote,  // (editing) → parent emette window event
  onCreaPraticaFromNote,   // (editing) → parent emette window event
  onPrintSheet,            // (note) → stampa scheda completa
}) {
  const [editForm, setEditForm] = useState(initialForm);
  const [editFormInitial, setEditFormInitial] = useState(initialForm);
  const [replyText, setReplyText] = useState("");
  const [pendingClientName, setPendingClientName] = useState("");

  // Quando il parent apre una nuova nota (initialForm cambia), resetto
  // local state. Non è strettamente necessario perché il parent smonta
  // e rimonta DiaryNoteEditor via key/open, ma è una safeguard.
  useEffect(() => {
    setEditForm(initialForm);
    setEditFormInitial(initialForm);
    setReplyText("");
    setPendingClientName("");
  }, [initialForm]);

  const toastAfter = useCallback((kind, msg) => {
    setTimeout(() => { try { (kind === "error" ? toast.error : toast.success)(msg); } catch { /* ignore */ } }, 0);
  }, []);

  // Auto-resolve di cliente appena creato esternamente (dialog globale)
  useEffect(() => {
    if (!pendingClientName) return;
    const q = pendingClientName.trim().toLowerCase();
    if (!q) return;
    const match = clients.find((c) => {
      const label = (c.fullName || c.name || "").toLowerCase();
      return label === q || label.startsWith(q) || q.startsWith(label);
    });
    if (match) {
      setEditForm((f) => f ? {
        ...f,
        clientId: match.id,
        clientName: match.fullName || match.name,
        clientQuery: match.fullName || match.name,
        caseId: "",
      } : f);
      setPendingClientName("");
    }
  }, [clients, pendingClientName]);

  const isDirty = useMemo(() => {
    // Dirty-check leggero: compariamo SOLO i campi semanticamente
    // rilevanti per la conferma di chiusura senza salvataggio.
    // Evitiamo JSON.stringify (girerebbe ad ogni keystroke nelle
    // textarea e selette, costando ~0.05-0.2ms per render — sembra
    // poco ma in dev StrictMode raddoppia e si somma al jitter).
    if (!editForm || !editFormInitial) return false;
    const keys = ["content", "clientQuery", "clientId", "clientName", "caseId",
                  "areaType", "contactType", "contactDate", "contactTime",
                  "destinatarioId"];
    for (let i = 0; i < keys.length; i++) {
      if ((editForm[keys[i]] || "") !== (editFormInitial[keys[i]] || "")) return true;
    }
    return false;
  }, [editForm, editFormInitial]);

  const requestClose = useCallback((opts = {}) => {
    if (!opts.force && isDirty) {
      const ok = window.confirm("Ci sono modifiche non salvate. Vuoi davvero chiudere senza salvare?");
      if (!ok) return;
    }
    onClose?.();
  }, [isDirty, onClose]);

  const activeCasesForClient = useMemo(() => {
    const cid = editForm?.clientId;
    if (!cid) return [];
    return cases.filter((p) => p.clientId === cid && isPracticeActive(p));
  }, [cases, editForm?.clientId]);

  const resolveClientFromQuery = useCallback((q) => {
    if (!q) return { id: "", name: "" };
    const lower = q.toLowerCase().trim();
    const exact = clients.find((c) => (c.fullName || c.name || "").toLowerCase() === lower);
    if (exact) return { id: exact.id, name: exact.fullName || exact.name };
    return { id: "", name: q };
  }, [clients]);

  const handleNewClient = useCallback(() => {
    const typed = (editForm?.clientQuery || editForm?.clientName || "").trim();
    setPendingClientName(typed);
    if (typeof onOpenNewClient === "function") {
      try { onOpenNewClient(typed); } catch { /* ignore */ }
    } else {
      toastAfter("error", "Creazione cliente non disponibile in questo contesto");
    }
  }, [editForm, onOpenNewClient, toastAfter]);

  const handleSave = useCallback(async () => {
    if (!editForm) return;
    if (!editForm.content.trim()) { toastAfter("error", "Inserisci il contenuto della nota"); return; }
    const resolved = resolveClientFromQuery(editForm.clientQuery || editForm.clientName || "");
    const finalClientId = editForm.clientId || resolved.id;
    if (!finalClientId) { toastAfter("error", "Cliente obbligatorio: scegli dal menu o digita un nome esatto"); return; }
    if (!editForm.areaType) { toastAfter("error", "Area Operativa obbligatoria"); return; }
    try {
      const payload = {
        contactDate: editForm.contactDate,
        contactTime: editForm.contactTime,
        contactType: editForm.contactType,
        clientId: finalClientId,
        clientName: clientById.get(finalClientId) || editForm.clientName || resolved.name,
        caseId: editForm.caseId || null,
        areaType: editForm.areaType,
        content: editForm.content,
        operatorId: editForm.operatorId || currentOperator?.id || null,
        destinatarioId: editForm.destinatarioId || null,
        requiresAction: true,
      };
      let saved;
      const isNew = !editing?.id;
      if (editing?.id) {
        saved = await api.updateContact(editing.id, payload);
        toastAfter("success", "Nota aggiornata");
      } else {
        saved = await api.createContact(payload);
        toastAfter("success", "Nota creata");
      }
      setEditFormInitial(editForm); // resetta dirty-check
      onSaved?.(saved, isNew);
    } catch (e) {
      toastAfter("error", e?.message || "Errore salvataggio");
    }
  }, [editForm, editing, clientById, currentOperator, resolveClientFromQuery, toastAfter, onSaved]);

  const handleReply = useCallback(async () => {
    if (!editing?.id) return;
    if (!replyText.trim()) { toastAfter("error", "Scrivi una risposta"); return; }
    try {
      const updated = await api.replyToContact(editing.id, replyText.trim());
      setReplyText("");
      onReplied?.(updated);
      toastAfter("success", "Risposta inviata");
    } catch (e) {
      toastAfter("error", e?.message || "Errore invio risposta");
    }
  }, [editing, replyText, onReplied, toastAfter]);

  const editingHistory = useMemo(() => {
    const arr = Array.isArray(editing?.history) ? editing.history : [];
    const replies = arr.filter((h) => (h.action || "") === "reply");
    const audit = arr.filter((h) => (h.action || "") !== "reply");
    return { replies, audit };
  }, [editing]);

  if (!editForm) return null;

  return (
    <InlineModal
      open={open}
      onClose={requestClose}
      wide
      closeOnBackdrop={false}
      title={editing?.id ? "Modifica nota" : "Nuova nota"}
      footer={
        <>
          {editing?.id && (
            <button type="button" onClick={() => onPrintSheet?.(editing)} style={btnSecondary} data-testid="diary-modal-print-sheet">
              🖨 Stampa scheda
            </button>
          )}
          <button type="button" onClick={() => requestClose()} style={btnSecondary}>Annulla</button>
          <button type="button" onClick={handleSave} style={btnPrimary} data-testid="diary-save-btn">Salva</button>
        </>
      }
    >
      <>
        <ClientsDataList clients={clients} />

        <div className="diary-edit-grid">
          {/* Riga 1: Tipo · Data · Ora · Operatore (lockato) */}
          <div>
            <label style={labelStyle}>Tipo contatto</label>
            <select value={editForm.contactType} onChange={(e) => setEditForm((f) => ({ ...f, contactType: e.target.value }))} style={inputStyle} data-testid="diary-modal-type">
              <ContactTypeSelectOptions />
            </select>
          </div>
          <div>
            <label style={labelStyle}>Data</label>
            <input type="date" value={editForm.contactDate} onChange={(e) => setEditForm((f) => ({ ...f, contactDate: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Ora</label>
            <input type="time" value={editForm.contactTime} onChange={(e) => setEditForm((f) => ({ ...f, contactTime: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Operatore (autore)</label>
            <input
              type="text"
              value={getOperatorName(editForm.operatorId) || editForm.operatorName || ""}
              readOnly
              disabled
              style={inputReadOnlyStyle}
              data-testid="diary-modal-operator-locked"
              title="L'operatore è bloccato all'utente loggato"
            />
          </div>

          {/* Riga 2: Cliente · Area · Pratica */}
          <div className="diary-col-half">
            <label style={labelStyle}>Cliente *</label>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                type="text"
                list="diary-clients-list"
                placeholder="Digita nome o ragione sociale…"
                value={editForm.clientQuery ?? editForm.clientName ?? ""}
                onChange={(e) => {
                  const q = e.target.value;
                  const r = resolveClientFromQuery(q);
                  setEditForm((f) => ({ ...f, clientQuery: q, clientId: r.id, clientName: r.name, caseId: r.id !== f.clientId ? "" : f.caseId }));
                }}
                style={{ ...inputStyle, flex: 1 }}
                data-testid="diary-modal-client-search"
                required
              />
              <button
                type="button"
                onClick={handleNewClient}
                style={{ ...btnSecondary, whiteSpace: "nowrap" }}
                title="Crea un nuovo cliente con il nome digitato"
                data-testid="diary-modal-new-client"
              >
                + Nuovo Cliente
              </button>
            </div>
            <div style={{ fontSize: 10, color: editForm.clientId ? "#0d9488" : "#dc2626", marginTop: 2 }}>
              {editForm.clientId ? `✓ ${clientById.get(editForm.clientId) || editForm.clientName}` : "Cliente non risolto — scegli dall'elenco o crealo"}
            </div>
          </div>
          <div>
            <label style={labelStyle}>Area operativa *</label>
            <select
              value={editForm.areaType || ""}
              onChange={(e) => setEditForm((f) => ({ ...f, areaType: e.target.value }))}
              style={inputStyle}
              data-testid="diary-modal-area"
              required
            >
              <AreaSelectOptions />
            </select>
          </div>
          <div>
            <label style={labelStyle}>Pratica collegata</label>
            <select
              value={editForm.caseId || ""}
              onChange={(e) => setEditForm((f) => ({ ...f, caseId: e.target.value }))}
              style={inputStyle}
              disabled={!editForm.clientId}
              data-testid="diary-modal-case"
            >
              <option value="">{editForm.clientId ? (activeCasesForClient.length ? "— Nessuna —" : "Nessuna pratica attiva") : "Seleziona prima il cliente"}</option>
              {activeCasesForClient.map((p) => {
                const lbl = `${p.practiceType || p.serviceType || "Pratica"} · ${(p.subject || p.description || p.id || "").slice(0, 40)}`;
                return <option key={p.id} value={p.id}>{lbl}</option>;
              })}
            </select>
          </div>

          {/* Riga 3: Destinatario */}
          <div className="diary-col-half">
            <label style={labelStyle}>Destinatario</label>
            <select value={editForm.destinatarioId} onChange={(e) => setEditForm((f) => ({ ...f, destinatarioId: e.target.value }))} style={inputStyle} data-testid="diary-modal-destinatario">
              <OperatorsSelectOptions operators={operators} />
            </select>
          </div>

          {/* Riga 4: Contenuto full — 15/07/2026: IsolatedTextarea per
              evitare re-render dell'intero modal ad ogni carattere
              (Fase 2.1 audit prestazionale). onCommit propaga al
              parent con debounce 300ms + immediato al blur. */}
          <div className="diary-col-full">
            <label style={labelStyle}>Contenuto *</label>
            <IsolatedTextarea
              value={editForm.content}
              onCommit={(v) => setEditForm((f) => ({ ...f, content: v }))}
              style={{ ...inputStyle, minHeight: 110, fontFamily: "inherit", resize: "vertical" }}
              placeholder="Descrivi il contatto/registrazione…"
              data-testid="diary-modal-content"
            />
          </div>

          <div className="diary-col-full" style={{ fontSize: 11, color: "#64748b", marginTop: -4 }}>
            La nota nasce <strong>attiva</strong>. Usa il pulsante ✓ nella riga per chiuderla quando l&apos;attività è terminata.
          </div>

          {/* Sezioni visibili solo in MODIFICA */}
          {editing?.id && (
            <>
              <div className="diary-col-full" style={{ borderTop: "1px solid #e2e8f0", marginTop: 8, paddingTop: 12 }}>
                <label style={{ ...labelStyle, marginBottom: 6 }}>Azioni operative</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button type="button" onClick={() => onCreaScadenzaFromNote?.(editing)} style={btnAccent} data-testid="diary-modal-create-scadenza">
                    📅 Crea Scadenza
                  </button>
                  <button type="button" onClick={() => onCreaPraticaFromNote?.(editing)} style={btnAccent} data-testid="diary-modal-create-pratica">
                    📋 Crea Pratica
                  </button>
                </div>
              </div>

              <div className="diary-col-full" style={{ marginTop: 4 }}>
                <label style={{ ...labelStyle, marginBottom: 6 }}>Risposte operative ({editingHistory.replies.length})</label>
                <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 4, padding: 8, background: "#fafafa" }} data-testid="diary-modal-replies">
                  {editingHistory.replies.length === 0
                    ? <div style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>Nessuna risposta ancora.</div>
                    : editingHistory.replies.map((r, i) => (
                      <div key={`${r.timestamp}-${i}`} style={{ borderLeft: "3px solid #1d4ed8", paddingLeft: 8, marginBottom: 8, fontSize: 12 }}>
                        <div style={{ fontWeight: 600, color: "#1e3a8a" }}>
                          {getOperatorName(r.operatorId) || r.operatorName || "Operatore"}
                          <span style={{ marginLeft: 6, color: "#64748b", fontWeight: 400, fontSize: 11 }}>{r.timestamp ? new Date(r.timestamp).toLocaleString("it-IT") : ""}</span>
                        </div>
                        <div style={{ marginTop: 2, whiteSpace: "pre-wrap" }}>{r.note || r.content || ""}</div>
                      </div>
                    ))
                  }
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                  <IsolatedTextarea
                    value={replyText}
                    onCommit={setReplyText}
                    placeholder="Scrivi una risposta operativa…"
                    style={{ ...inputStyle, minHeight: 50, fontFamily: "inherit", resize: "vertical", flex: 1 }}
                    data-testid="diary-modal-reply-text"
                  />
                  <button type="button" onClick={handleReply} style={btnPrimary} data-testid="diary-modal-reply-send" disabled={!replyText.trim()}>
                    Invia
                  </button>
                </div>
              </div>

              <div className="diary-col-full" style={{ marginTop: 4 }}>
                <label style={{ ...labelStyle, marginBottom: 6 }}>Cronologia ({editingHistory.audit.length})</label>
                <div style={{ maxHeight: 160, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 4, padding: 8, background: "#fafafa", fontSize: 11 }} data-testid="diary-modal-history">
                  {editingHistory.audit.length === 0
                    ? <div style={{ color: "#94a3b8", fontStyle: "italic" }}>Nessuna modifica registrata.</div>
                    : editingHistory.audit.map((h, i) => (
                      <div key={`${h.timestamp}-${i}`} style={{ padding: "3px 0", borderBottom: "1px dotted #cbd5e1" }}>
                        <strong>{h.timestamp ? new Date(h.timestamp).toLocaleString("it-IT") : "—"}</strong>
                        {" · "}
                        <span style={{ color: "#1e40af" }}>{h.action || "—"}</span>
                        {" · "}
                        <span style={{ color: "#475569" }}>{getOperatorName(h.operatorId) || h.operatorName || "Sistema"}</span>
                        {h.note && <span style={{ marginLeft: 6, color: "#475569" }}>— {h.note}</span>}
                      </div>
                    ))
                  }
                </div>
              </div>
            </>
          )}
        </div>
      </>
    </InlineModal>
  );
}


export default function DiarioGiornalieroTabRebuilt({
  clients = [],
  cases = [],
  operators = [],
  currentOperator,
  onOpenNewClient,
  // 11/06/2026 — Quick-action flag from App.js. Click on "Diario
  // Giornaliero" in the top action bar sets pendingQuickAction ===
  // "diario:new"; this tab opens the new-note dialog immediately upon
  // mount and clears the flag via consumeQuickAction (one-shot).
  pendingQuickAction = null,
  consumeQuickAction = () => {},
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtri
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  // Default filter "Stato" = "open" (In corso) — richiesta utente
  // 28/02/2026. L'utente può comunque cambiare il filtro manualmente.
  const [filterStatus, setFilterStatus] = useState("open");
  const [filterOperator, setFilterOperator] = useState("");
  const [filterDestinatario, setFilterDestinatario] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [filterYear, setFilterYear] = useState(() => String(new Date().getFullYear()));
  const [filterMonthFrom, setFilterMonthFrom] = useState("all");
  const [filterMonthTo, setFilterMonthTo] = useState("all");

  // Pagina
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem("diary_rebuilt_pagesize"), 10);
      return Number.isFinite(v) && PAGE_SIZES.includes(v) ? v : 50;
    } catch { return 50; }
  });
  const setPageSizePersist = useCallback((n) => {
    setPageSize(n);
    try { localStorage.setItem("diary_rebuilt_pagesize", String(n)); } catch { /* ignore */ }
    setPage(1);
  }, []);

  // Modali
  const [editing, setEditing] = useState(null); // null | {id} | {id:null}
  // Wave 2 perf refactor (28/06/2026): `editForm`, `editFormInitial`,
  // `replyText` SONO STATI SPOSTATI dentro `<DiaryNoteEditor>` per
  // evitare il re-render della tabella di 50 righe ad ogni keystroke.
  // Il parent ora detiene SOLO il puntatore alla nota in modifica
  // (`editing`) e lo snapshot iniziale del form (`editingInitialForm`)
  // costruito una volta sola in openCreate/openEdit.
  const [editingInitialForm, setEditingInitialForm] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [completionDate, setCompletionDate] = useState("");
  // 27/02/2026 — Stati dei mini-form Crea Scadenza/Crea Pratica rimossi:
  // ora le azioni emettono eventi window per aprire il dialog completo
  // gestito da ScadenzeTab / App.js. Nessuno stato locale richiesto qui.

  // Lookup helpers
  const operatorById = useMemo(() => {
    const m = new Map();
    operators.forEach((o) => m.set(o.id, o.fullName || o.name || o.id));
    return m;
  }, [operators]);
  const getOperatorName = useCallback((id) => operatorById.get(id) || "", [operatorById]);
  const clientById = useMemo(() => {
    const m = new Map();
    clients.forEach((c) => m.set(c.id, c.fullName || c.name || ""));
    return m;
  }, [clients]);
  const getClientLabel = useCallback((id, fallback) => clientById.get(id) || fallback || "—", [clientById]);
  const caseById = useMemo(() => {
    const m = new Map();
    cases.forEach((p) => m.set(p.id, p));
    return m;
  }, [cases]);
  const getCaseLabel = useCallback((id) => {
    if (!id) return "—";
    const c = caseById.get(id);
    if (!c) return id;
    const parts = [c.practiceType || c.serviceType, c.subject || c.description, c.cardNumber || c.number].filter(Boolean);
    return parts.length ? parts.join(" · ") : (c.id || "—");
  }, [caseById]);

  const loadItems = useCallback(() => {
    setLoading(true); setError(null);
    api.getContacts()
      .then((data) => {
        const list = Array.isArray(data) ? data : (data?.items || []);
        setItems(list);
        setLoading(false);
      })
      .catch((e) => { setError(e?.message || "Errore"); setLoading(false); });
  }, []);
  useEffect(() => { loadItems(); }, [loadItems]);

  // 27/02/2026 — Ricarica i contatti quando il backend ha appena
  // appeso un audit entry alla nota (creata Pratica o Scadenza da
  // dialog completo). Evento globale emesso da App.js / ScadenzeTab.
  useEffect(() => {
    const onRefresh = () => { loadItems(); };
    window.addEventListener("gi:diary-refresh", onRefresh);
    return () => window.removeEventListener("gi:diary-refresh", onRefresh);
  }, [loadItems]);

  const yearOptions = useMemo(() => {
    const s = new Set();
    s.add(String(new Date().getFullYear()));
    items.forEach((c) => {
      const d = (c.contactDate || "").slice(0, 10);
      if (d && /^\d{4}-/.test(d)) s.add(d.slice(0, 4));
    });
    return Array.from(s).sort((a, b) => b.localeCompare(a));
  }, [items]);

  const monthRange = useMemo(() => {
    if (filterYear === "all") return null;
    const mFrom = filterMonthFrom !== "all" ? filterMonthFrom : null;
    const mToRaw = filterMonthTo !== "all" ? filterMonthTo : null;
    let m1 = mFrom, m2 = mToRaw || mFrom;
    if (m1 && m2 && m1 > m2) { const t = m1; m1 = m2; m2 = t; }
    const from = m1 ? `${filterYear}-${m1}-01` : `${filterYear}-01-01`;
    const to = m2 ? `${filterYear}-${m2}-${lastDayOfMonth(filterYear, m2)}` : `${filterYear}-12-31`;
    return { from, to };
  }, [filterYear, filterMonthFrom, filterMonthTo]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return items.filter((c) => {
      if (filterType !== "all" && c.contactType !== filterType) return false;
      if (filterStatus === "open" && c.completedAt) return false;
      if (filterStatus === "done" && !c.completedAt) return false;
      if (filterOperator && c.operatorId !== filterOperator) return false;
      if (filterDestinatario && c.destinatarioId !== filterDestinatario) return false;
      if (filterClient && c.clientId !== filterClient) return false;
      const d = (c.contactDate || "").slice(0, 10);
      if (monthRange && (!d || d < monthRange.from || d > monthRange.to)) return false;
      if (q) {
        const hay = `${c.clientName || ""} ${c.content || ""} ${c.contactType || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, searchQuery, filterType, filterStatus, filterOperator, filterDestinatario, filterClient, monthRange]);

  const sorted = useMemo(() => {
    const cp = [...filtered];
    cp.sort((a, b) => {
      const av = `${a.contactDate || ""} ${a.contactTime || ""}`;
      const bv = `${b.contactDate || ""} ${b.contactTime || ""}`;
      return av < bv ? 1 : av > bv ? -1 : 0;
    });
    return cp;
  }, [filtered]);

  useEffect(() => { setPage(1); }, [searchQuery, filterType, filterStatus, filterOperator, filterDestinatario, filterClient, filterYear, filterMonthFrom, filterMonthTo]);
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
  const paged = useMemo(() => sorted.slice((page - 1) * pageSize, page * pageSize), [sorted, page, pageSize]);

  const openCount = useMemo(() => items.filter((c) => !c.completedAt).length, [items]);
  const doneCount = items.length - openCount;

  const hasActiveFilters = Boolean(
    searchQuery.trim() || filterType !== "all" || filterStatus !== "all" ||
    filterOperator || filterDestinatario || filterClient ||
    filterYear !== "all" || filterMonthFrom !== "all" || filterMonthTo !== "all"
  );
  const resetFilters = useCallback(() => {
    setSearchQuery(""); setFilterType("all"); setFilterStatus("all");
    setFilterOperator(""); setFilterDestinatario(""); setFilterClient("");
    setFilterYear(String(new Date().getFullYear()));
    setFilterMonthFrom("all"); setFilterMonthTo("all");
  }, []);

  const toastAfter = useCallback((kind, msg) => {
    setTimeout(() => { try { (kind === "error" ? toast.error : toast.success)(msg); } catch { /* ignore */ } }, 0);
  }, []);

  // ─── Create / Edit ─────────────────────────────────────────────────
  // Wave 2 perf refactor: il "form" vive ora dentro <DiaryNoteEditor>.
  // openCreate/openEdit costruiscono lo snapshot iniziale e lo passano
  // al child come prop. Save / Reply / Crea Scadenza / Crea Pratica
  // tornano in callback dal child, qui sotto.
  const openCreate = useCallback(() => {
    const fresh = emptyNoteForm(currentOperator);
    setEditing({ id: null });
    setEditingInitialForm(fresh);
  }, [currentOperator]);

  // 11/06/2026 — Prop-based quick-action consumer. Click sul bottone
  // "Diario Giornaliero" in App.js apre il dialog "Nuova nota" subito.
  useEffect(() => {
    if (pendingQuickAction === "diario:new") {
      openCreate();
      consumeQuickAction("diario:new");
    }
  }, [pendingQuickAction]);

  const openEdit = useCallback((c) => {
    const initial = {
      contactDate: (c.contactDate || todayISO()).slice(0, 10),
      contactTime: c.contactTime || nowHHMM(),
      contactType: c.contactType || "Telefonata",
      clientId: c.clientId || "",
      clientName: c.clientName || "",
      clientQuery: c.clientName || "",
      caseId: c.caseId || "",
      areaType: c.areaType || "",
      content: c.content || "",
      operatorId: c.operatorId || currentOperator?.id || "",
      operatorName: c.operatorName || currentOperator?.fullName || "",
      destinatarioId: c.destinatarioId || "",
    };
    setEditing(c);
    setEditingInitialForm(initial);
  }, [currentOperator]);

  const closeEditModal = useCallback(() => {
    // Il dirty-check è ora dentro <DiaryNoteEditor> (window.confirm()),
    // quindi qui ci limitiamo a chiudere quando il child ce lo dice.
    setEditing(null);
    setEditingInitialForm(null);
  }, []);

  // Callback chiamata dal child dopo una create/update riuscita.
  const handleNoteSaved = useCallback((saved, isNew) => {
    setItems((prev) => isNew ? [saved, ...prev] : prev.map((x) => (x.id === saved.id ? saved : x)));
    closeEditModal();
  }, [closeEditModal]);

  // Callback chiamata dal child dopo una reply riuscita.
  const handleNoteReplied = useCallback((updated) => {
    setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    setEditing(updated); // aggiorna il puntatore così il child mostra la nuova history
  }, []);

  // Stampa scheda completa
  const handlePrintFull = useCallback((c) => {
    const ok = printOperationalSheet(c, { getOperatorName, getClientLabel, getCaseLabel });
    if (!ok) toastAfter("error", "Apertura finestra bloccata");
  }, [getOperatorName, getClientLabel, getCaseLabel, toastAfter]);

  // ─── Crea Scadenza Clienti dalla nota (27/02/2026, fix v2 per bug
  //     blocking UI dopo apertura) ──────────────────────────────────
  const handleCreaScadenzaFromNote = useCallback((note) => {
    if (!note?.id) return;
    const detail = {
      clientId: note.clientId || "",
      clientName: note.clientName || "",
      practiceType: note.areaType === "studio" ? "studio" : "agenzia",
      deadlineType: note.contactType ? `Follow-up ${note.contactType}` : "Follow-up",
      notes: (note.content || "").slice(0, 500),
      assignedOperatorId: note.destinatarioId || note.operatorId || "",
      sourceContactId: note.id,
    };
    closeEditModal();
    setTimeout(() => {
      try {
        window.dispatchEvent(new CustomEvent("gi:open-extra-deadline-from-diary", { detail }));
      } catch (e) {
        console.error("dispatch open-extra-deadline-from-diary failed:", e);
      }
    }, 0);
  }, [closeEditModal]);

  // ─── Crea Pratica completa dalla nota (27/02/2026) ────────────────
  const handleCreaPraticaFromNote = useCallback((note) => {
    if (!note?.id) return;
    const detail = {
      clientId: note.clientId || "",
      clientName: note.clientName || "",
      subject: (note.content || "").slice(0, 120),
      description: (note.content || ""),
      practiceArea: note.areaType === "studio" ? "Gestionale Studio" : "Gestionale Agenzia",
      practiceType: note.areaType === "studio" ? "Gestionale Studio" : "Gestionale Agenzia",
      assignedOperatorId: note.destinatarioId || note.operatorId || "",
      sourceContactId: note.id,
    };
    closeEditModal();
    setTimeout(() => {
      try {
        window.dispatchEvent(new CustomEvent("gi:open-case-from-diary", { detail }));
      } catch (e) {
        console.error("dispatch open-case-from-diary failed:", e);
      }
    }, 0);
  }, [closeEditModal]);

  // ─── Complete / Reopen ─────────────────────────────────────────────
  const openComplete = useCallback((c) => { setCompleting(c); setCompletionDate(todayISO()); }, []);
  const closeCompleteModal = useCallback(() => { setCompleting(null); setCompletionDate(""); }, []);
  const confirmComplete = useCallback(async () => {
    if (!completing) return;
    try {
      const updated = await api.completeContact(completing.id, completionDate || todayISO());
      setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      closeCompleteModal();
      toastAfter("success", "Nota chiusa");
    } catch (e) {
      toastAfter("error", e?.message || "Errore chiusura");
    }
  }, [completing, completionDate, closeCompleteModal, toastAfter]);
  const handleReopen = useCallback(async (c) => {
    try {
      const updated = await api.reopenContact(c.id);
      setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      toastAfter("success", "Nota riaperta");
    } catch (e) {
      toastAfter("error", e?.message || "Errore riapertura");
    }
  }, [toastAfter]);

  // ─── Crea Scadenza / Crea Pratica dalla nota: gestiti da
  //     `handleCreaScadenzaFromNote` / `handleCreaPraticaFromNote`
  //     (vedi sopra), invocati dal child <DiaryNoteEditor> via prop.

  // ─── UI ────────────────────────────────────────────────────────────
  const typeBadge = (type) => {
    const b = CONTACT_TYPES.find((t) => t.value === type) || CONTACT_TYPES[0];
    return <span style={{ background: b.color, color: b.text, padding: "2px 6px", borderRadius: 4, fontSize: 11 }}>{type || "—"}</span>;
  };

  return (
    <div data-testid="diario-giornaliero-tab">
      {/* Stili responsive locali per la modale */}
      <style>{`
        .diary-edit-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
        .diary-edit-grid > .diary-col-half { grid-column: span 2; }
        .diary-edit-grid > .diary-col-full { grid-column: 1 / -1; }
        @media (max-width: 1023px) {
          .diary-edit-grid { grid-template-columns: repeat(2, 1fr); }
          .diary-edit-grid > .diary-col-half { grid-column: span 2; }
        }
        @media (max-width: 639px) {
          .diary-edit-grid { grid-template-columns: 1fr; }
          .diary-edit-grid > .diary-col-half { grid-column: 1; }
        }
      `}</style>

      {/* Toolbar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ flex: "1 1 220px", minWidth: 200 }}>
          <label style={labelStyle}>Cerca</label>
          {/* 16/07/2026 — DebouncedSearchInput (Fase 2.3 audit): il
              filtro dell'archivio non viene più applicato ad ogni
              keystroke ma dopo 200ms di inattività. Preparato per
              scalare quando l'archivio contatti crescerà. */}
          <DebouncedSearchInput
            externalValue={searchQuery}
            onDebounced={setSearchQuery}
            placeholder="Cliente / contenuto / tipo"
            style={inputStyle}
            testId="diary-search"
          />
        </div>
        <div>
          <label style={labelStyle}>Tipo</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={inputStyle} data-testid="diary-type">
            <option value="all">Tutti</option>
            {CONTACT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.value}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Stato</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={inputStyle} data-testid="diary-status">
            <option value="all">Tutti</option>
            <option value="open">In corso</option>
            <option value="done">Chiuse</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Operatore</label>
          <select value={filterOperator} onChange={(e) => setFilterOperator(e.target.value)} style={inputStyle} data-testid="diary-filter-operator">
            <option value="">Tutti</option>
            {operators.map((o) => <option key={o.id} value={o.id}>{o.fullName || o.name || o.id}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Destinatario</label>
          <select value={filterDestinatario} onChange={(e) => setFilterDestinatario(e.target.value)} style={inputStyle} data-testid="diary-filter-destinatario">
            <option value="">Tutti</option>
            {operators.map((o) => <option key={o.id} value={o.id}>{o.fullName || o.name || o.id}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Cliente</label>
          <select value={filterClient} onChange={(e) => setFilterClient(e.target.value)} style={inputStyle}>
            <option value="">Tutti</option>
            {clients.slice(0, 500).map((c) => <option key={c.id} value={c.id}>{c.fullName || c.name}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Anno</label>
          <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} style={inputStyle} data-testid="diary-year-filter">
            <option value="all">Tutti</option>
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Mese da</label>
          <select value={filterMonthFrom} onChange={(e) => setFilterMonthFrom(e.target.value)} disabled={filterYear === "all"} style={inputStyle} data-testid="diary-month-from-filter">
            <option value="all">Tutti i mesi</option>
            {MONTHS_IT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Mese a</label>
          <select value={filterMonthTo} onChange={(e) => setFilterMonthTo(e.target.value)} disabled={filterYear === "all" || filterMonthFrom === "all"} style={inputStyle} data-testid="diary-month-to-filter">
            <option value="all">—</option>
            {MONTHS_IT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
          {hasActiveFilters && <button type="button" onClick={resetFilters} style={btnSecondary}>Reset filtri</button>}
          <button type="button" onClick={openCreate} style={btnPrimary} data-testid="diary-new-btn">+ Nuova nota</button>
        </div>
      </div>

      <div style={{ fontSize: 13, color: "#475569", marginBottom: 8 }}>
        {loading ? "Caricamento…" : `${sorted.length} note (${openCount} in corso · ${doneCount} chiuse · ${items.length} totali)`}
        {error && <span style={{ color: "#dc2626", marginLeft: 8 }}>· Errore: {error}</span>}
      </div>

      <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 4 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f1f5f9", textAlign: "left" }}>
              <th style={{ padding: "8px", borderBottom: "1px solid #cbd5e1" }}>Data</th>
              <th style={{ padding: "8px", borderBottom: "1px solid #cbd5e1" }}>Ora</th>
              <th style={{ padding: "8px", borderBottom: "1px solid #cbd5e1" }}>Tipo</th>
              <th style={{ padding: "8px", borderBottom: "1px solid #cbd5e1" }}>Cliente</th>
              <th style={{ padding: "8px", borderBottom: "1px solid #cbd5e1" }}>Contenuto</th>
              <th style={{ padding: "8px", borderBottom: "1px solid #cbd5e1" }}>Operatore</th>
              <th style={{ padding: "8px", borderBottom: "1px solid #cbd5e1" }}>Destinatario</th>
              <th style={{ padding: "8px", borderBottom: "1px solid #cbd5e1" }}>Stato</th>
              <th style={{ padding: "8px", borderBottom: "1px solid #cbd5e1", textAlign: "right" }}>Azioni</th>
            </tr>
          </thead>
          <tbody hidden={paged.length !== 0}>
            <tr><td colSpan={9} style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>
              {loading ? "Caricamento…" : "Nessuna nota corrisponde ai filtri."}
            </td></tr>
          </tbody>
          {paged.map((c) => (
            <tbody key={c.id}>
              <tr style={{ borderBottom: "1px solid #e2e8f0" }} data-testid={`diary-row-${c.id}`}>
                <td style={{ padding: "6px 8px" }}>{(c.contactDate || "").slice(0, 10)}</td>
                <td style={{ padding: "6px 8px" }}>{c.contactTime || "—"}</td>
                <td style={{ padding: "6px 8px" }}>{typeBadge(c.contactType)}</td>
                <td style={{ padding: "6px 8px" }}>{c.clientName || getClientLabel(c.clientId) || "—"}</td>
                <td style={{ padding: "6px 8px", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.content || "—"}</td>
                <td style={{ padding: "6px 8px" }}>{getOperatorName(c.operatorId) || c.operatorName || "—"}</td>
                <td style={{ padding: "6px 8px" }}>{getOperatorName(c.destinatarioId) || "—"}</td>
                <td style={{ padding: "6px 8px" }}>
                  {c.completedAt
                    ? <span style={{ padding: "2px 6px", background: "#dcfce7", color: "#166534", borderRadius: 4, fontSize: 11 }}>Chiusa</span>
                    : <span style={{ padding: "2px 6px", background: "#dbeafe", color: "#1e40af", borderRadius: 4, fontSize: 11 }}>In corso</span>}
                </td>
                <td style={{ padding: "6px 8px", textAlign: "right", whiteSpace: "nowrap" }}>
                  {c.completedAt
                    ? <button type="button" onClick={() => handleReopen(c)} style={btnGhost} data-testid={`diary-reopen-${c.id}`}>
                        <span aria-hidden="true">↻</span> <span style={{ marginLeft: 4 }}>Riapri</span>
                      </button>
                    : <button type="button" onClick={() => openComplete(c)} style={{ ...btnGhost, color: "#047857" }} data-testid={`diary-complete-${c.id}`}>
                        <span aria-hidden="true">✓</span> <span style={{ marginLeft: 4 }}>Chiudi</span>
                      </button>}
                  <button type="button" onClick={() => handlePrintFull(c)} style={btnGhost} data-testid={`diary-print-${c.id}`}>
                    <span aria-hidden="true">🖨</span> <span style={{ marginLeft: 4 }}>Stampa</span>
                  </button>
                  <button type="button" onClick={() => openEdit(c)} style={btnGhost} data-testid={`diary-edit-${c.id}`}>
                    <span aria-hidden="true">✏</span> <span style={{ marginLeft: 4 }}>Modifica</span>
                  </button>
                </td>
              </tr>
            </tbody>
          ))}
        </table>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 0", fontSize: 12, color: "#475569", flexWrap: "wrap" }}>
        <span>Pagina</span>
        <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} style={btnSecondary}>«</button>
        <select value={page} onChange={(e) => setPage(parseInt(e.target.value, 10) || 1)} style={{ ...inputStyle, width: 70 }}>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <span>di {totalPages}</span>
        <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={btnSecondary}>»</button>
        <span style={{ marginLeft: 12 }}>righe</span>
        <select value={pageSize} onChange={(e) => setPageSizePersist(parseInt(e.target.value, 10))} style={{ ...inputStyle, width: 70 }}>
          {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <span style={{ marginLeft: "auto" }}>
          {sorted.length === 0 ? "0 risultati" : `${(page - 1) * pageSize + 1} – ${Math.min(sorted.length, page * pageSize)} di ${sorted.length}`}
        </span>
      </div>

      {/* ─── Modale Nuova / Modifica nota (Wave 2 perf: estratta) ─ */}
      <DiaryNoteEditor
        open={!!editing}
        editing={editing}
        initialForm={editingInitialForm}
        clients={clients}
        cases={cases}
        operators={operators}
        currentOperator={currentOperator}
        clientById={clientById}
        getOperatorName={getOperatorName}
        getClientLabel={getClientLabel}
        onClose={closeEditModal}
        onSaved={handleNoteSaved}
        onReplied={handleNoteReplied}
        onOpenNewClient={onOpenNewClient}
        onCreaScadenzaFromNote={handleCreaScadenzaFromNote}
        onCreaPraticaFromNote={handleCreaPraticaFromNote}
        onPrintSheet={handlePrintFull}
      />

      {/* Modale chiusura */}
      <InlineModal
        open={!!completing}
        onClose={closeCompleteModal}
        title={`Chiudi nota — ${completing?.clientName || ""}`}
        footer={
          <>
            <button type="button" onClick={closeCompleteModal} style={btnSecondary}>Annulla</button>
            <button type="button" onClick={confirmComplete} style={btnPrimary} data-testid="diary-confirm-complete-btn">Conferma</button>
          </>
        }
      >
        <div>
          <label style={labelStyle}>Data chiusura</label>
          <input type="date" value={completionDate} onChange={(e) => setCompletionDate(e.target.value)} style={inputStyle} data-testid="diary-completion-date" />
        </div>
      </InlineModal>

      {/* 27/02/2026 — Mini-modali "Crea Scadenza dalla nota" e
          "Crea Pratica dalla nota" RIMOSSE: ora il Diario apre
          rispettivamente il dialog completo "Nuova Scadenza Clienti"
          (gestito da ScadenzeTab) e il CaseDialog (gestito da App.js)
          via eventi window, con dati pre-compilati e collegamento
          bidirezionale lato backend. */}
    </div>
  );
}
